import { useTranslation } from 'react-i18next';
import { Controller, type UseFormReturn } from 'react-hook-form';
import { Input } from '@/shared/ui/inputs/Input';
import { Textarea } from '@/shared/ui/inputs/Textarea';
import { Select } from '@/shared/ui/selects/Select';
import { Section } from '@/shared/ui/layout/Section';
import { HelpTooltip } from '@/shared/ui/feedback/HelpTooltip';
import { REGION_VALUES } from '@/shared/utils/regions';
import { env } from '@/shared/lib/env';
import { handleUppercaseChange } from '@/shared/lib/forms';
import type { AddressFormValues } from '@/features/addresses/modules/types';

type Props = {
  form: UseFormReturn<AddressFormValues>;
  onSubmit: (values: AddressFormValues) => void;
  id?: string;
};

// Passport series mirrors admin: 9 chars total (2 uppercase Latin + 7 digits).
const PASSPORT_MAX = 9;

export const FeaturesAddressesAddressForm = ({ form, onSubmit, id }: Props) => {
  const { t } = useTranslation('addresses');
  const { t: tParcel } = useTranslation('parcelForm');
  const { register, control, formState, handleSubmit } = form;
  const errors = formState.errors;

  const regionOptions = REGION_VALUES.map((value) => ({ value, label: t(`regions:${value}`) }));
  const passportField = register('passport_series');

  return (
    <form id={id} onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
      <Section title={t('sectionAddress')}>
        <Input
          label={t('form.label')}
          labelAdornment={<HelpTooltip text={t('form.labelHelp')} />}
          required
          placeholder={t('form.labelPlaceholder')}
          error={errors.label?.message ? t(errors.label.message) : undefined}
          {...register('label')}
        />
        <Input
          label={t('form.addressName')}
          labelAdornment={<HelpTooltip text={t('form.addressNameHelp')} />}
          required
          placeholder={t('form.addressNamePlaceholder')}
          error={errors.address_name?.message ? t(errors.address_name.message) : undefined}
          {...register('address_name')}
        />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Controller
            control={control}
            name="region"
            render={({ field }) => (
              <Select
                label={t('form.region')}
                required
                options={regionOptions}
                value={field.value}
                onChange={(e) => field.onChange(e.target.value)}
                error={errors.region?.message ? t(errors.region.message) : undefined}
              />
            )}
          />
          <Input
            label={t('form.city')}
            required
            error={errors.city?.message ? t(errors.city.message) : undefined}
            {...register('city')}
          />
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Input
            label={t('form.street')}
            required
            error={errors.street?.message ? t(errors.street.message) : undefined}
            {...register('street')}
          />
          <Input
            label={t('form.building')}
            required
            error={errors.building?.message ? t(errors.building.message) : undefined}
            {...register('building')}
          />
          <Input
            label={t('form.apartment')}
            error={errors.apartment?.message ? t(errors.apartment.message) : undefined}
            {...register('apartment')}
          />
        </div>
        <Textarea
          label={t('form.additionalInfo')}
          rows={3}
          error={errors.additional_info?.message ? t(errors.additional_info.message) : undefined}
          {...register('additional_info')}
        />
        <label className="flex cursor-pointer items-center gap-2.5 text-sm font-medium text-ink-soft">
          <input
            type="checkbox"
            className="h-4 w-4 cursor-pointer rounded border-slate-300 text-brand-600 focus:ring-brand-200"
            {...register('is_default')}
          />
          {t('form.isDefault')}
        </label>
      </Section>

      <Section title={t('sectionPersonal')}>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Input
            label={tParcel('fields.lastName')}
            required
            error={errors.last_name?.message ? t(errors.last_name.message) : undefined}
            {...register('last_name')}
          />
          <Input
            label={tParcel('fields.firstName')}
            required
            error={errors.first_name?.message ? t(errors.first_name.message) : undefined}
            {...register('first_name')}
          />
          <Input
            label={tParcel('fields.middleName')}
            required
            error={errors.middle_name?.message ? t(errors.middle_name.message) : undefined}
            {...register('middle_name')}
          />
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Input
            label={tParcel('fields.phone')}
            required
            inputMode="tel"
            placeholder={tParcel('fields.phonePlaceholder')}
            error={errors.phone_number?.message ? t(errors.phone_number.message) : undefined}
            {...register('phone_number')}
          />
          <Input
            label={tParcel('fields.passport')}
            required
            placeholder={tParcel('fields.passportPlaceholder')}
            autoCapitalize="characters"
            maxLength={PASSPORT_MAX}
            error={errors.passport_series?.message ? t(errors.passport_series.message) : undefined}
            {...passportField}
            onChange={handleUppercaseChange(passportField.onChange)}
          />
        </div>
        <Input
          label={tParcel('fields.pinfl')}
          required
          inputMode="numeric"
          maxLength={env.pinflLength}
          placeholder={tParcel('fields.pinflPlaceholder', { length: env.pinflLength })}
          error={
            errors.pinfl?.message
              ? t(errors.pinfl.message, { length: env.pinflLength })
              : undefined
          }
          {...register('pinfl')}
        />
      </Section>
    </form>
  );
};
